import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { supabase } from "../lib/supabase";
import { Input, Button, useToast, PageHeader, FormField, PageLayout } from "@dawa/ui";

export default function Settings() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<{ name: string; role: string; password: string }>();
  const { addToast, Toasts } = useToast();
  const [initializing, setInitializing] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const {
          data: { user },
          error,
        } = await supabase.auth.getUser();
        if (error) throw error;
        reset({
          name: (user?.user_metadata as any)?.name || "",
          role: (user?.user_metadata as any)?.role || "",
          password: "",
        });
        setLoadError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Erreur lors du chargement du profil";
        setLoadError(message);
        addToast(message, "error");
      } finally {
        setInitializing(false);
      }
    })();
  }, [reset, addToast]);

  return (
    <form
      className="mx-auto max-w-md space-y-4 p-6"
      onSubmit={handleSubmit(async (v) => {
        setSubmitError(null);
        try {
          const { error } = await supabase.auth.updateUser({
            data: { name: v.name, role: v.role },
            ...(v.password ? { password: v.password } : {}),
          });
          if (error) throw error;
          if (v.password) {
            addToast("Profil mis à jour. Reconnectez-vous si nécessaire.", "success");
          } else {
            addToast("Profil mis à jour", "success");
          }
          reset((current) => ({ ...current, password: "" }));
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Impossible de mettre à jour le profil";
          setSubmitError(message);
          addToast(message, "error");
        }
      })}
    >
      <PageHeader title="Paramètres" subtitle="Gérez votre profil et vos préférences." />
      {loadError && <p className="text-sm text-danger">{loadError}</p>}
      <FormField label="Nom" htmlFor="name">
        <Input id="name" placeholder="Nom" disabled={initializing} {...register("name")} />
      </FormField>
      <FormField label="Rôle" htmlFor="role">
        <Input id="role" placeholder="Rôle" disabled={initializing} {...register("role")} />
      </FormField>
      <FormField label="Mot de passe" htmlFor="password" help="Laissez vide pour ne pas modifier">
        <Input
          id="password"
          placeholder="Mot de passe"
          type="password"
          disabled={initializing}
          {...register("password", { minLength: { value: 8, message: "8 caractères minimum" } })}
        />
      </FormField>
      {submitError && <p className="text-sm text-danger">{submitError}</p>}
      <Button disabled={isSubmitting || initializing} loading={isSubmitting}>
        Enregistrer
      </Button>
      <div className="pt-4">
        <h2 className="mb-2 text-lg font-semibold">Aide</h2>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            window.dispatchEvent(new Event("open:shortcuts"));
          }}
        >
          Raccourcis clavier (? )
        </Button>
      </div>
      <Toasts />
    </form>
  );
}
